/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();

      /** @type {HTMLElement} */
      this.price = this.section.querySelector('.js-product-price > .price');

      /** @type {HTMLElement} */
      this.priceValueEl = this.price.querySelector('[data-component-price-value]');

      /** @type {HTMLElement} */
      this.originalPriceEl = this.price.querySelector('[data-component-original-price]');

      /** @type {HTMLElement} */
      this.memberPriceEl = this.price.querySelector('[data-component-member-price]');

      this.getPriceData()

      this.showSalePrice = this.priceData?.showSalePrice || false;

      this.enableGlobalSale = this.priceData?.enableGlobalSale || false;

      this.globalDiscountPercentage = this.priceData?.globalDiscountPercentage || 0;

      this.enableIndividualProductSale = this.priceData?.enableIndividualProductSale || false;

      this.productDiscountPercentage = this.priceData?.productDiscountPercentage || 0;

      this.enableMemberDiscount = this.priceData?.enableMemberDiscount || false;

      this.memberDiscountPercentage = this.priceData?.memberDiscountPercentage || 0;

      this.memberClearanceDiscount = this.priceData?.memberClearanceDiscount || 0;
      this.hasMemberClearanceDiscount = this.priceData?.hasMemberClearanceDiscount || false;
      this.hasMemberDiscount = this.priceData?.hasMemberDiscount || false;

      this.memberSaleDiscount = this.priceData?.memberSaleDiscount || 0;
      this.hasMemberSaleDiscount = this.priceData?.hasMemberSaleDiscount || false;

      this.enableMemberRebateDisplay = this.priceData?.enableMemberRebateDisplay || false;

      this.memberRebateAmount = this.priceData?.memberRebateAmount || 0;

      this.showCurrencyCode = this.priceData?.showCurrencyCode || false;

      this.moneyFormat = this.priceData?.moneyFormat || '{{ amount }}';

      this.updateAvailability();
      this.syncColorLabels();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const selectedOptions = this.getSelectedOptions();
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.data.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );

      if (this.variant) {
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      this.syncColorLabels();

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset;
      const selectedOptions = this.getSelectedOptions();

      if (availabilityMethod === 'selection') {
        let currVariant = this.variant;
        if (!this.variant) {
          currVariant = { options: selectedOptions };
        }

        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Syncs colour option label text (e.g. "COLOR: Olive") to the checked swatch.
     * Needed on first paint and when options change programmatically (OV default
     * variant only dispatches change on the last option, which is often Size).
     */
    syncColorLabels() {
      this.querySelectorAll('.option-selector').forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') return;

        const colorText = selector.querySelector('.js-color-text');
        if (!colorText) return;

        const checked = selector.querySelector('input.js-option:checked');
        if (!checked) return;

        const valueEl = checked.nextElementSibling && checked.nextElementSibling.querySelector('.js-value');
        if (valueEl) {
          colorText.textContent = valueEl.textContent;
        }
      });
    }

    /**
     * @deprecated Prefer syncColorLabels(); kept for any external callers.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const picker = evt.target && evt.target.closest('variant-picker');
      if (picker && typeof picker.syncColorLabels === 'function') {
        picker.syncColorLabels();
        return;
      }

      const selector = evt.target.closest('.option-selector');
      if (!selector || selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      const valueEl = evt.target.nextElementSibling && evt.target.nextElementSibling.querySelector('.js-value');
      if (valueEl) {
        colorText.textContent = valueEl.textContent;
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.js-product-price > .price');
      if (!this.price) return;

      this.priceValueEl = this.priceValueEl || this.price.querySelector('[data-component-price-value]');
      this.originalPriceEl = this.originalPriceEl || this.price.querySelector('[data-component-original-price]');
      this.memberPriceEl = this.memberPriceEl || this.price.querySelector('[data-component-member-price]');

      const priceValueEl = this.priceValueEl;
      const originalPriceEl = this.originalPriceEl;
      const memberPriceEl = this.memberPriceEl;

      if (this.variant) {
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        priceValueEl.innerHTML = this.data.formatted[this.variant.id].price;

        const utils = window.outdoorvitals.utils;

        // Determine effective display price (after any active sale discounts)
        let effectiveDisplayPrice = this.variant.price;
        if (this.variant.compare_at_price > this.variant.price) {
          // variant.price is already the sale price
        } else if (this.enableIndividualProductSale && this.productDiscountPercentage > 0) {
          effectiveDisplayPrice = utils.getDiscountedPrice(this.variant.price, this.productDiscountPercentage);
        } else if (this.enableGlobalSale && this.globalDiscountPercentage > 0) {
          effectiveDisplayPrice = utils.getDiscountedPrice(this.variant.price, this.globalDiscountPercentage);
        }

        if (priceWasEl) {
          const originalPrice = this.variant.compare_at_price > this.variant.price
            ? this.variant.compare_at_price
            : this.variant.price;

          priceValueEl.textContent = utils.replaceMoneyFormat(this.moneyFormat, utils.getDollarAmount(effectiveDisplayPrice));
          originalPriceEl.textContent = utils.replaceMoneyFormat(this.moneyFormat, utils.getDollarAmount(originalPrice));
        }

        // Update member price calculated off the effective (discounted) price
        let memberDiscountToApply = 0;
        if (this.enableMemberDiscount) {
          if (this.hasMemberSaleDiscount && this.memberSaleDiscount > 0) {
            memberDiscountToApply = this.memberSaleDiscount;
          } else if (this.hasMemberClearanceDiscount && this.memberClearanceDiscount > 0) {
            memberDiscountToApply = this.memberClearanceDiscount;
          } else if (this.hasMemberDiscount && this.memberDiscountPercentage > 0) {
            memberDiscountToApply = this.memberDiscountPercentage;
          }
        }
        if (memberPriceEl && memberDiscountToApply > 0) {
          const memberDiscountedPrice = utils.getDiscountedPrice(this.variant.price, memberDiscountToApply);
          memberPriceEl.textContent = utils.getMemberPriceText(
            utils.replaceMoneyFormat(this.moneyFormat, utils.getDollarAmount(memberDiscountedPrice))
          );
        }

        if (memberPriceEl) {
          const productEligibleForMemberPrice = this.hasMemberDiscount
            || this.hasMemberClearanceDiscount
            || this.hasMemberSaleDiscount;
          memberPriceEl.parentNode.hidden = !this.enableMemberDiscount
            || !productEligibleForMemberPrice
            || !this.variant.available;
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        if (!unitPriceEl || !priceWasEl) return;
        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        priceWasEl.hidden = !this.showSalePrice || !this.variant.available;

        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant;
      this.price.querySelector('.price__no-variant').hidden = this.variant;
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**querySelector
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    getPriceData() {
      const priceDataEl = this.price?.querySelector('[type="application/json"]');

      if (priceDataEl) {
        this.priceData = JSON.parse(priceDataEl.textContent);
      }      
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
